import clsx from "clsx";
import {
  type Dispatch,
  FunctionComponent,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { IntroPhase } from "@story/data-centers-hub/components/IntroCovers";
import {
  flowCue,
  noteColumn,
  scrollCueIcon,
  scrollCuePath,
} from "@story/data-centers-hub/components/IntroCovers/IntroCovers.css";
import ArizonaSlide from "@story/data-centers-hub/components/slides/ArizonaSlide";
import CreditsSlide from "@story/data-centers-hub/components/slides/CreditsSlide";
import LoudounSlide from "@story/data-centers-hub/components/slides/LoudounSlide";
import TitleSlide from "@story/data-centers-hub/components/slides/TitleSlide";
import UtahSlide from "@story/data-centers-hub/components/slides/UtahSlide";
import { introWipeMs } from "@story/data-centers-hub/config/intro";
import type { EditorNoteBlock } from "@story/data-centers-hub/config/editorNoteBlocks";
import { resolveHubSlideLink } from "@story/data-centers-hub/config/resolveHubSlideLink";
import {
  deckSlides,
  slides,
  type DeckSlideId,
} from "@story/data-centers-hub/config/slides";
import { NoteBlocks } from "@story/data-centers-hub/components/IntroCovers/NoteBlocks";
import { useHubMediaWarmup } from "@story/data-centers-hub/hooks/useHubMediaWarmup";
import { useHubMotionOn } from "@story/data-centers-hub/hooks/useHubMotionEnabled";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import { ArizonaSlideMeta_feedContentResource$key } from "src/__generated__/relay/ArizonaSlideMeta_feedContentResource.graphql";
import { LoudounSlideMeta_feedContentResource$key } from "src/__generated__/relay/LoudounSlideMeta_feedContentResource.graphql";
import { UtahSlideMeta_feedContentResource$key } from "src/__generated__/relay/UtahSlideMeta_feedContentResource.graphql";
import getBrowserInfo from "src/utils/get-browser-info";

import ScrollBallast from "./ScrollBallast";
import * as styles from "./SlideDeck.css";

const SCROLL_CHEVRON_PATH = "M5 9 L12 16 L19 9";

/** One intentional flick / notch before the next chapter can fire. */
const NAV_WHEEL_THRESHOLD_PX = 64;
/** Quiet period that ends a trackpad gesture so the next flick can count. */
const NAV_WHEEL_IDLE_MS = 140;
/** Floor for fade hops so smooth scroll can settle before another go(). */
const NAV_FADE_LOCK_MS = 560;
/**
 * Touch: suppress native scroll/chrome before committing a chapter hop.
 * Must preventDefault as soon as vertical intent is clear — a 10px window let
 * iOS start document scroll + `scroll-snap` before `go()`, leaving scrollY
 * between panes and blocking the next hop (chrome-park then gave up after 1 try).
 */
const NAV_TOUCH_PREVENT_PX = 2;
const NAV_TOUCH_COMMIT_PX = 20;
/** Keep chrome re-park off briefly after finger-up — Safari resizes then. */
const NAV_TOUCH_CHROME_GRACE_MS = 450;
/**
 * Survive soft remounts (tab freeze / Suspense) without a full reload — those
 * reset React state to the intro while the reader is mid-deck.
 */
const HUB_SLIDE_STORAGE_KEY = "verge:data-centers-hub-slide";
const DECK_SLIDE_IDS: ReadonlyArray<DeckSlideId> = [
  "note",
  "title",
  "virginia",
  "utah",
  "arizona",
  "credits",
];

function isDeckSlideId(value: string | null | undefined): value is DeckSlideId {
  return Boolean(
    value && (DECK_SLIDE_IDS as ReadonlyArray<string>).includes(value),
  );
}

function readStoredHubSlide(): DeckSlideId | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const value = window.sessionStorage.getItem(HUB_SLIDE_STORAGE_KEY);
    return isDeckSlideId(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredHubSlide(id: DeckSlideId): void {
  try {
    window.sessionStorage.setItem(HUB_SLIDE_STORAGE_KEY, id);
  } catch {
    // Private mode / quota — in-memory state still works for the session.
  }
}

function wheelDeltaY(event: WheelEvent): number {
  if (event.deltaMode === 1) {
    return event.deltaY * 16;
  }
  if (event.deltaMode === 2) {
    return event.deltaY * window.innerHeight;
  }
  return event.deltaY;
}

/** Chapters that mount lazily and play a clip wipe (share/comments stay mounted once up). */
type WipeChapterId = "virginia" | "utah" | "arizona";
const WIPE_CHAPTERS: readonly WipeChapterId[] = [
  "virginia",
  "utah",
  "arizona",
];

/** Ordered wipe / fade neighbors — undirected edges that use intro wipe ms. */
const WIPE_EDGES: ReadonlyArray<readonly [DeckSlideId, DeckSlideId]> = [
  ["note", "title"],
  ["title", "virginia"],
  ["virginia", "utah"],
  ["utah", "arizona"],
];

function isWipeChapter(id: string): id is WipeChapterId {
  return (WIPE_CHAPTERS as ReadonlyArray<string>).includes(id);
}

function isWipeTransition(from: DeckSlideId, to: DeckSlideId): boolean {
  if (from === "credits" || to === "credits") {
    return true;
  }
  return WIPE_EDGES.some(
    ([a, b]) => (from === a && to === b) || (from === b && to === a),
  );
}

function navLockMs(
  from: DeckSlideId,
  to: DeckSlideId,
  wipeMs: number,
): number {
  if (!isWipeTransition(from, to)) {
    return NAV_FADE_LOCK_MS;
  }
  // Android IO often re-fires a neighbor when the animation ends. iOS paid
  // that same +400 as a dead first swipe after the wipe looked finished.
  const settlePad = getBrowserInfo()?.android ? 400 : 80;
  return wipeMs + settlePad;
}

/** Wipe chapters at or before `landing` (credits mounts all three). */
function wipesThrough(landing: DeckSlideId): WipeChapterId[] {
  if (landing === "credits") {
    return [...WIPE_CHAPTERS];
  }
  if (!isWipeChapter(landing)) {
    return [];
  }
  return WIPE_CHAPTERS.slice(0, WIPE_CHAPTERS.indexOf(landing) + 1);
}

/**
 * Depart = reverse wipe toward an earlier chapter (only while we are the
 * tracked outgoing). Hold = a later chapter is up — stay painted underneath
 * for as long as that is true (not only while `outgoingId` is set). Relying
 * on outgoing alone dropped underlays mid-settle and flashed the title.
 */
function wipeCoverage(
  outgoingId: DeckSlideId | null,
  activeSlideId: DeckSlideId,
  id: WipeChapterId,
): { depart: boolean; hold: boolean } {
  // title → virginia → utah → arizona → credits (note is never a depart target)
  const chain: DeckSlideId[] = [
    "title",
    "virginia",
    "utah",
    "arizona",
    "credits",
  ];
  const from = chain.indexOf(id);
  const to = chain.indexOf(activeSlideId);
  if (activeSlideId === id || from < 0) {
    return { depart: false, hold: false };
  }
  // Note sits under the stage — treat it as earlier than every wipe chapter
  // so chapter→intro runs the normal depart close (not a held underlay).
  if (
    outgoingId === id &&
    (activeSlideId === "note" || (to >= 0 && to < from))
  ) {
    return { depart: true, hold: false };
  }
  if (to > from) {
    return { depart: false, hold: true };
  }
  return { depart: false, hold: false };
}

/**
 * Chapter painted under the intro cover wipe / return. `note` is not on the
 * chapter wipe chain, so `wipeCoverage` drops hold the moment `openNote`
 * flips `activeSlideId` — without this the underlay unpaints (~80ms) and the
 * return flashes black before the squares close.
 */
function introCoverUnderlay(
  landing: DeckSlideId,
): WipeChapterId | null {
  if (isWipeChapter(landing)) {
    return landing;
  }
  if (landing === "credits") {
    return "arizona";
  }
  return null;
}

/**
 * Mark an in-slide scroller so the deck wheel handler can yield without
 * reading computed style. `getComputedStyle` on every ancestor of every
 * wheel event forced a style flush for the whole session.
 */
const NESTED_SCROLL_ATTR = "data-hub-nested-scroll";

/** Let a marked in-slide scroller eat the wheel before the deck. */
function nestedScrollerCanAbsorb(
  target: EventTarget | null,
  deltaY: number,
): boolean {
  if (!(target instanceof Element) || deltaY === 0) {
    return false;
  }
  const node = target.closest(`[${NESTED_SCROLL_ATTR}]`);
  if (!(node instanceof HTMLElement)) {
    return false;
  }
  if (node.scrollHeight <= node.clientHeight + 1) {
    return false;
  }
  const top = node.scrollTop;
  const max = node.scrollHeight - node.clientHeight;
  if (deltaY < 0 && top > 0) {
    return true;
  }
  if (deltaY > 0 && top < max - 1) {
    return true;
  }
  return false;
}

function NoteArrow() {
  return (
    <svg className={scrollCueIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path className={scrollCuePath} d={SCROLL_CHEVRON_PATH} />
    </svg>
  );
}

/** Stacked intro note — full copy in flow; continue jumps to the title. */
function StaticNote({
  blocks,
  active,
  onSlideLink,
}: {
  blocks: ReadonlyArray<EditorNoteBlock>;
  active: boolean;
  onSlideLink?: (id: DeckSlideId) => void;
}) {
  return (
    <section
      id="hub-slide-note"
      className={styles.staticNote}
      aria-hidden={active ? undefined : true}
    >
      <div
        className={styles.staticNoteScroll}
        onClick={(event) => {
          if (!onSlideLink) {
            return;
          }
          const anchor = (event.target as Element | null)?.closest?.("a");
          if (!(anchor instanceof HTMLAnchorElement)) {
            return;
          }
          const href = anchor.getAttribute("href");
          if (!href) {
            return;
          }
          const slideId = resolveHubSlideLink(href);
          if (!slideId || slideId === "note") {
            return;
          }
          event.preventDefault();
          onSlideLink(slideId);
        }}
      >
        <div className={noteColumn}>
          <NoteBlocks blocks={blocks} />
          <button
            type="button"
            className={flowCue}
            aria-label="Continue"
            onClick={() => {
              document
                .getElementById("hub-slide-title")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <NoteArrow />
          </button>
        </div>
      </div>
    </section>
  );
}

interface Props {
  activeSlideId: DeckSlideId;
  onActiveSlideChange: (id: DeckSlideId) => void;
  /** Loudoun feature node — lockup / share / comment meta. */
  loudounMeta?: LoudounSlideMeta_feedContentResource$key | null;
  /** Utah feature node — lockup / share / comment meta. */
  utahMeta?: UtahSlideMeta_feedContentResource$key | null;
  /** Arizona feature node — lockup bylines / dek. */
  arizonaMeta?: ArizonaSlideMeta_feedContentResource$key | null;
  /** Editor's note blocks. Opens on the black squares; scroll starts the shift. */
  editorNoteBlocks?: ReadonlyArray<EditorNoteBlock>;
  /**
   * Parent fills this with the deck `go` stepper so PackageNav prev/next can
   * drive the same path as wheel / keyboard (wipes + nav lock).
   */
  stepRef?: MutableRefObject<((delta: number) => void) | null>;
  /**
   * Absolute hop (intro → chapter, or mid-deck skip). Note links use this so
   * a Loudoun href can run the cover wipe and land on Virginia.
   */
  goToRef?: MutableRefObject<((id: DeckSlideId) => void) | null>;
  /** True while a wipe / hop lock is active — PackageNav disables arrows. */
  onStepBusyChange?: (busy: boolean) => void;
  /**
   * True after a note deep-link (or mid-deck resume) skipped the title —
   * PackageNav shows a quick return to the intro.
   */
  onIntroEscapeChange?: (escaped: boolean) => void;
}

/**
 * Full-viewport slide host.
 *
 * Motion on: fixed mosaic. `activeSlideId` is the source of truth; document
 * scroll is a snap track that follows it (wheel / touch / keyboard `go()`).
 * IntersectionObserver only confirms a pending hop — it does not promote
 * chapters when the browser moves scrollY on its own. Note ↔ title is the
 * square wipe; title ↔ Loudoun, Virginia ↔ Utah, and Utah ↔ Arizona are clip
 * wipes. Motion off: the same order stacked, free scroll.
 */
const SlideDeck: FunctionComponent<Props> = ({
  activeSlideId,
  onActiveSlideChange,
  loudounMeta = null,
  utahMeta = null,
  arizonaMeta = null,
  editorNoteBlocks = [],
  stepRef,
  goToRef,
  onStepBusyChange,
  onIntroEscapeChange,
}) => {
  const labelId = useId();
  const hasNote = editorNoteBlocks.length > 0;
  const deck = useMemo(() => deckSlides(hasNote), [hasNote]);
  /** Wipe chapters that exist as snap panes — never prefetch off-deck. */
  const deckWipeChapters = useMemo(
    () => WIPE_CHAPTERS.filter((id) => deck.some((slide) => slide.id === id)),
    [deck],
  );
  const activeLabel =
    deck.find((slide) => slide.id === activeSlideId)?.label ?? "Title";
  const motionOn = useHubMotionOn();
  const narrow = useHubNarrowViewport();
  const wipeMs = introWipeMs(narrow);
  useHubMediaWarmup(activeSlideId, deck);
  const paneRefs = useRef<Partial<Record<DeckSlideId, HTMLDivElement | null>>>(
    {},
  );
  const bindPaneRef = useRef(
    Object.fromEntries(
      (
        [
          "note",
          ...slides.map((slide) => slide.id),
          "credits",
        ] as DeckSlideId[]
      ).map(
        (id) => [
          id,
          (node: HTMLDivElement | null) => {
            paneRefs.current[id] = node;
          },
        ],
      ),
    ) as Record<DeckSlideId, (node: HTMLDivElement | null) => void>,
  );
  /** Keyboard / gesture target. Cleared once that snap pane is in view. */
  const pendingSlideRef = useRef<DeckSlideId | null>(null);
  /**
   * Slide observers read this so they are not torn down on every chapter
   * change. Reconnecting cleared their ratio map mid-scroll.
   */
  const activeSlideIdRef = useRef(activeSlideId);
  activeSlideIdRef.current = activeSlideId;
  /** Blocks a second chapter hop until the current scroll / wipe has settled. */
  const navLockUntilRef = useRef(0);
  /** Mirrors `navLockUntilRef` for PackageNav disable (refs don’t re-render). */
  const [hopLocked, setHopLocked] = useState(false);
  const hopLockTimerRef = useRef<number | null>(null);
  /**
   * True while we are writing scrollY. Browser / chrome idle moves are
   * snapped back to `activeSlideId`; without this flag that fight itself.
   */
  const programmaticScrollRef = useRef(false);
  const programmaticScrollClearRef = useRef(0);
  /**
   * Finger is down (or just lifted). Chrome-resize re-park must not run across
   * this window — that was the missed iOS swipe→hop path.
   */
  const touchGestureRef = useRef(false);
  const touchGestureClearRef = useRef(0);
  /**
   * Mid-swipe chrome toggle / soft native pan can leave scrollY off the active
   * pane. Touch-end / hop-lock end / deferred retries flush a capped drift
   * check — never an open settle loop (that remounted credits every ~1s).
   */
  const needsChromeParkRef = useRef(false);
  /** Set by the scroll-sync effect; touch-end clear calls it. */
  const flushChromeParkRef = useRef<() => void>(() => {});
  /** Parks attempted in the current drift episode (resets when aligned). */
  const chromeParkAttemptsRef = useRef(0);
  const chromeParkLastAtRef = useRef(0);

  const markTouchGesture = useCallback(() => {
    touchGestureRef.current = true;
    window.clearTimeout(touchGestureClearRef.current);
  }, []);

  const clearTouchGestureSoon = useCallback(() => {
    window.clearTimeout(touchGestureClearRef.current);
    touchGestureClearRef.current = window.setTimeout(() => {
      touchGestureRef.current = false;
      if (needsChromeParkRef.current) {
        flushChromeParkRef.current();
      }
    }, NAV_TOUCH_CHROME_GRACE_MS);
  }, []);

  /** Document scroll follows chapter state — never the other way around. */
  const parkScrollTo = useCallback(
    (id: DeckSlideId, behavior: ScrollBehavior = "auto") => {
      const pane = paneRefs.current[id];
      if (!pane) {
        return;
      }
      programmaticScrollRef.current = true;
      window.clearTimeout(programmaticScrollClearRef.current);
      window.scrollTo({ top: pane.offsetTop, behavior });
      if (behavior === "auto") {
        // Longer than a chrome-toggle / snap settle — 64ms was clearing while
        // iOS was still rewriting scrollY, so the sync listener fought back.
        programmaticScrollClearRef.current = window.setTimeout(() => {
          programmaticScrollRef.current = false;
        }, 320);
      }
      // `smooth` stays programmatic until the pending hop is confirmed.
    },
    [],
  );
  /**
   * Wipe chapters mount when nearby and stay mounted (grow-only). Whole-slide
   * prune fought prefetch IO and flickered the stage; decoder reclaim is
   * `mediaWarm` → TitleCellMedia releasing `src`, not unmounting the chapter.
   */
  const [mountedWipes, setMountedWipes] = useState(
    () => new Set<WipeChapterId>(),
  );
  const mountWipes = useCallback((ids: readonly WipeChapterId[]) => {
    if (ids.length === 0) {
      return;
    }
    setMountedWipes((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);
  const wipeNeighborhood = useCallback(
    (
      anchor: DeckSlideId,
      outgoing: DeckSlideId | null,
      allowed: readonly WipeChapterId[],
    ): Set<WipeChapterId> => {
      const keep = new Set<WipeChapterId>();
      const allow = (id: WipeChapterId) => {
        if (allowed.includes(id)) {
          keep.add(id);
        }
      };
      const addAround = (id: DeckSlideId) => {
        if (id === "note" || id === "title") {
          allow("virginia");
          return;
        }
        if (id === "credits") {
          allow("arizona");
          return;
        }
        if (!isWipeChapter(id)) {
          return;
        }
        const index = allowed.indexOf(id);
        if (index < 0) {
          return;
        }
        for (
          let i = Math.max(0, index - 1);
          i <= Math.min(allowed.length - 1, index + 1);
          i += 1
        ) {
          const chapter = allowed[i];
          if (chapter) {
            allow(chapter);
          }
        }
      };
      addAround(anchor);
      if (outgoing) {
        addAround(outgoing);
      }
      return keep;
    },
    [],
  );
  const [trackedSlideId, setTrackedSlideId] = useState(activeSlideId);
  /** Slide we just left. Stays up until its transition into the next one ends. */
  const [outgoingId, setOutgoingId] = useState<DeckSlideId | null>(null);
  /**
   * At wipe-end the top clip can drop one frame while the underlay is still
   * unmuted for the overlay reveal. Latch mute from `wipeMs` so that frame
   * cannot show the previous chapter — and do not clear when `outgoingId`
   * drops (that was undoing the latch mid-settle). Cleared synchronously on
   * the next hop’s first paint so a stale latch cannot blank the new underlay.
   */
  const [wipeEndMute, setWipeEndMute] = useState(false);
  if (trackedSlideId !== activeSlideId) {
    setTrackedSlideId(activeSlideId);
    // Wipe depart only exists in the snap tree. Tracking it while stacked
    // leaves a stale outgoing that the first motion-on paint tries to finish.
    setOutgoingId(motionOn ? trackedSlideId : null);
    // End-mute from the *previous* hop must not survive into this paint —
    // it was blanking the new underlay (Utah→Arizona black frame) for a
    // tick before the wipeEndMute effect could clear it.
    setWipeEndMute(false);
  }
  const mediaNeighborhood = motionOn
    ? wipeNeighborhood(activeSlideId, outgoingId, deckWipeChapters)
    : null;

  // Restored mid-deck landings must not remount into the note hold — that was
  // the idle "jump back to intro" after a soft remount.
  const [introPhase, setIntroPhase] = useState<IntroPhase>(() =>
    editorNoteBlocks.length > 0 && activeSlideId === "note"
      ? "hold"
      : "done",
  );
  const introPhaseRef = useRef(introPhase);
  introPhaseRef.current = introPhase;

  if (mediaNeighborhood) {
    const needed = deckWipeChapters.filter(
      (id) => mediaNeighborhood.has(id) && !mountedWipes.has(id),
    );
    if (needed.length > 0) {
      mountWipes(needed);
    }
  }
  // IntroCovers unmounts once the wipe finishes, so the note's scroll offset
  // has to live here until a scroll back up remounts it.
  const noteScrollRef = useRef(0);
  const rememberNoteScroll = useCallback((top: number) => {
    noteScrollRef.current = top;
  }, []);

  // Deck is withheld until session restore, so the first mount can already
  // be mid-chapter — mount wipe chapters open. Cleared after commit so later
  // remounts of a single chapter (prefetch) still play their enter wipe.
  const landingResumeRef = useRef(activeSlideId !== "note");
  // Mid-deck resume skipped the note hold — same chrome return as a deep link.
  const resumedPastTitleRef = useRef(
    hasNote && activeSlideId !== "note" && activeSlideId !== "title",
  );
  useEffect(() => {
    if (resumedPastTitleRef.current) {
      resumedPastTitleRef.current = false;
      onIntroEscapeChange?.(true);
    }
  }, [onIntroEscapeChange]);
  useEffect(() => {
    landingResumeRef.current = false;
  }, []);
  const resumeMount =
    landingResumeRef.current ||
    (introPhase === "hold" && activeSlideId !== "note");

  // Park once under a restored mid-deck landing — never on later hops.
  const didLandingParkRef = useRef(false);
  useLayoutEffect(() => {
    if (didLandingParkRef.current) {
      return;
    }
    if (!motionOn || activeSlideId === "note") {
      return;
    }
    if (!landingResumeRef.current && introPhaseRef.current !== "hold") {
      return;
    }
    didLandingParkRef.current = true;
    if (introPhaseRef.current === "hold") {
      setIntroPhase("done");
    }
    pendingSlideRef.current = activeSlideId;
    parkScrollTo(activeSlideId, "auto");
  }, [activeSlideId, motionOn, parkScrollTo]);

  /** Where the cover wipe lands — title by default; chapter skips override. */
  const introLandingRef = useRef<DeckSlideId>("title");

  const armHopBusy = useCallback((busyMs: number, lockMs: number) => {
    navLockUntilRef.current = performance.now() + lockMs;
    if (hopLockTimerRef.current != null) {
      window.clearTimeout(hopLockTimerRef.current);
    }
    setHopLocked(true);
    hopLockTimerRef.current = window.setTimeout(() => {
      hopLockTimerRef.current = null;
      setHopLocked(false);
      // Wipe finished — one chance to fix chrome-zeroed scrollY, not mid-clip.
      if (needsChromeParkRef.current) {
        flushChromeParkRef.current();
      }
    }, Math.max(busyMs, lockMs) + 32);
  }, []);

  const startIntro = useCallback(
    (landing: DeckSlideId = "title") => {
      const target = landing === "note" ? "title" : landing;
      introLandingRef.current = target;
      setIntroPhase((phase) => (phase === "hold" ? "wiping" : phase));
      // Mount the stack under the covers; non-landing chapters stay `muted`
      // for the wipe (see `transitionMute`) so Arizona skips do not flash
      // Virginia/Utah.
      if (isWipeChapter(target) || target === "credits") {
        mountWipes(wipesThrough(target));
      }
      if (target !== "title") {
        pendingSlideRef.current = target;
        onActiveSlideChange(target);
        // Skipped the mosaic — offer chrome return to the note.
        onIntroEscapeChange?.(true);
      }
      // Do not park here. `window.scrollTo` in the click’s user-gesture
      // window collapses iOS Safari’s URL bar mid-wipe and the stage reads
      // as a rescale. `finishIntro` parks after the covers settle (outside
      // that gesture); snap IO stays quiet while `introPhase !== "done"`.
    },
    [mountWipes, onActiveSlideChange, onIntroEscapeChange],
  );
  const finishIntro = useCallback(() => {
    const landing = introLandingRef.current;
    setIntroPhase("done");
    pendingSlideRef.current = null;
    onActiveSlideChange(landing);
    // After the wipe — deferred from the note-link / continue gesture so
    // iPhone does not hide the URL bar for a document scroll in that tap.
    parkScrollTo(landing, "auto");
    // One deferred settle if chrome zeroed scroll during the wipe gesture.
    chromeParkAttemptsRef.current = 0;
    needsChromeParkRef.current = true;
  }, [onActiveSlideChange, parkScrollTo]);
  const settleIntro = useCallback(() => {
    setIntroPhase("hold");
    parkScrollTo("note", "auto");
  }, [parkScrollTo]);
  /**
   * @param underlay — stop the covers are closing over. Deep-link return must
   * keep the departing chapter (not title) so `titleMuted` / `transitionMute`
   * do not flash the mosaic for a frame before the squares land.
   */
  const reopenIntro = useCallback(
    (underlay: DeckSlideId = "title") => {
      introLandingRef.current =
        underlay !== "note" && underlay !== "title" ? underlay : "title";
      setIntroPhase((phase) => {
        if (phase !== "done") {
          return phase;
        }
        return motionOn ? "returning" : "hold";
      });
    },
    [motionOn],
  );

  useEffect(() => {
    if (!hasNote && activeSlideId === "note") {
      onActiveSlideChange("title");
    }
  }, [hasNote, activeSlideId, onActiveSlideChange]);

  /**
   * Play/pause remounts a different scroll model (stacked sections ↔ snap
   * panes). Their offsetTops are not comparable, so nearest-scroll matching
   * lands between chapters and the wipe layer loses the active slide. Trust
   * activeSlideId, park the new tree on that pane, and hold IO until it is.
   */
  const prevMotionOnRef = useRef(motionOn);
  useLayoutEffect(() => {
    const wasMotionOn = prevMotionOnRef.current;
    prevMotionOnRef.current = motionOn;
    if (wasMotionOn === motionOn) {
      return;
    }

    navLockUntilRef.current = performance.now() + 120;

    const landingId = deck.some((slide) => slide.id === activeSlideId)
      ? activeSlideId
      : (deck[0]?.id ?? "title");

    setOutgoingId(null);
    setTrackedSlideId(landingId);
    if (landingId === "note" && hasNote) {
      setIntroPhase("hold");
      pendingSlideRef.current = null;
    } else {
      setIntroPhase("done");
      // IO ignores chapter changes until this pane is majority-visible.
      pendingSlideRef.current = landingId;
    }
    mountWipes(wipesThrough(landingId));

    const park = () => {
      if (motionOn) {
        parkScrollTo(landingId, "auto");
        return;
      }
      const target = document.getElementById(`hub-slide-${landingId}`);
      if (target) {
        window.scrollTo({ top: target.offsetTop, behavior: "auto" });
      }
    };
    park();
    // Snap + remount can leave scroll between panes on the first frame.
    const raf = requestAnimationFrame(park);
    return () => cancelAnimationFrame(raf);
  }, [motionOn, deck, hasNote, activeSlideId, mountWipes, parkScrollTo]);

  const virginia = slides.find((slide) => slide.id === "virginia");
  const utah = slides.find((slide) => slide.id === "utah");
  const titleSlide = slides[0];
  const arizona = slides.find(
    (slide): slide is (typeof slides)[number] & { id: "arizona" } =>
      slide.id === "arizona",
  );

  const openNote = useCallback(() => {
    if (!hasNote) {
      return;
    }
    const from = activeSlideId;
    onIntroEscapeChange?.(false);
    // Do not park yet — `window.scrollTo(note)` in lvh mode collapses the
    // chrome gap (`lvh − svh` → 0) so ballast pops out before the return
    // wipe. `settleIntro` parks after the covers / chapter depart settle.
    onActiveSlideChange("note");
    reopenIntro(from);
  }, [
    hasNote,
    activeSlideId,
    onActiveSlideChange,
    reopenIntro,
    onIntroEscapeChange,
  ]);

  /**
   * Absolute hop. From the note hold, any chapter id runs the cover wipe and
   * lands there (skipping title when the target is later). Mid-deck uses the
   * same wipe/fade lock as adjacent `go` steps.
   */
  const goTo = useCallback(
    (id: DeckSlideId) => {
      if (
        introPhaseRef.current === "wiping" ||
        introPhaseRef.current === "returning"
      ) {
        return;
      }
      if (!motionOn) {
        const target = document.getElementById(`hub-slide-${id}`);
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
        onActiveSlideChange(id);
        return;
      }
      if (performance.now() < navLockUntilRef.current) {
        return;
      }
      if (introPhaseRef.current === "hold" && activeSlideId === "note") {
        if (id === "note") {
          return;
        }
        armHopBusy(wipeMs, wipeMs);
        startIntro(id);
        return;
      }
      if (introPhaseRef.current !== "done") {
        return;
      }
      if (!deck.some((slide) => slide.id === id)) {
        return;
      }
      if (id === activeSlideId) {
        return;
      }
      if (id === "note") {
        openNote();
        return;
      }

      const lockMs = navLockMs(activeSlideId, id, wipeMs);
      // Arrow disable matches the wipe / fade length — not the post-wipe
      // settle pad on `navLockUntil` (that was keeping desktop timing on phone).
      const busyMs = isWipeTransition(activeSlideId, id)
        ? wipeMs
        : NAV_FADE_LOCK_MS;
      armHopBusy(busyMs, lockMs);

      pendingSlideRef.current = id;
      if (isWipeChapter(id) || id === "credits") {
        mountWipes(wipesThrough(id));
      }
      // Wipe hops: instant park. Smooth scroll toggles the iOS/Android URL bar
      // mid-clip; the chrome re-park then fought the next swipe.
      const wipeHop = isWipeTransition(activeSlideId, id);
      parkScrollTo(id, wipeHop ? "auto" : "smooth");
      onActiveSlideChange(id);
      // Allow one touch-end / chrome-timer drift fix — no mid-wipe timers.
      chromeParkAttemptsRef.current = 0;
      needsChromeParkRef.current = true;
    },
    [
      activeSlideId,
      deck,
      motionOn,
      onActiveSlideChange,
      openNote,
      mountWipes,
      wipeMs,
      parkScrollTo,
      startIntro,
      armHopBusy,
    ],
  );

  const go = useCallback(
    (delta: number) => {
      // Mid-wipe: ignore until the covers settle.
      if (
        introPhaseRef.current === "wiping" ||
        introPhaseRef.current === "returning"
      ) {
        return;
      }
      // Motion off: free document scroll only — no chapter carousel / wipes.
      if (!motionOn) {
        return;
      }
      if (performance.now() < navLockUntilRef.current) {
        return;
      }
      // Note hold: forward starts the cover wipe (same as the continue cue).
      // Wheel / arrows stay with the note scroller; PackageNav steps do not.
      if (introPhaseRef.current === "hold" && activeSlideId === "note") {
        if (delta > 0) {
          armHopBusy(wipeMs, wipeMs);
          startIntro("title");
        }
        return;
      }
      if (introPhaseRef.current !== "done") {
        return;
      }
      const index = Math.max(
        0,
        deck.findIndex((slide) => slide.id === activeSlideId),
      );
      // Clamp — no wrap from credits→intro or intro→credits.
      const next = index + delta;
      if (next < 0 || next >= deck.length) {
        return;
      }
      const slide = deck[next];
      if (!slide || slide.id === activeSlideId) {
        return;
      }
      goTo(slide.id);
    },
    [
      activeSlideId,
      deck,
      motionOn,
      wipeMs,
      startIntro,
      armHopBusy,
      goTo,
    ],
  );

  // Keep the parent ref current during render — an effect left the first
  // PackageNav click as a no-op until after paint.
  if (stepRef) {
    stepRef.current = go;
  }
  if (goToRef) {
    goToRef.current = goTo;
  }
  useEffect(() => {
    if (!stepRef) {
      return;
    }
    return () => {
      stepRef.current = null;
    };
  }, [stepRef]);
  useEffect(() => {
    if (!goToRef) {
      return;
    }
    return () => {
      goToRef.current = null;
    };
  }, [goToRef]);

  // Intro cover phases only — chapter underlay `outgoingId` pads past wipe
  // end (+400/+700) and was keeping arrows dark longer than the animation.
  const wipeBusy =
    introPhase === "wiping" || introPhase === "returning";
  const stepBusy = hopLocked || wipeBusy;

  useEffect(() => {
    onStepBusyChange?.(stepBusy);
  }, [stepBusy, onStepBusyChange]);

  useEffect(() => {
    return () => {
      if (hopLockTimerRef.current != null) {
        window.clearTimeout(hopLockTimerRef.current);
      }
      window.clearTimeout(touchGestureClearRef.current);
    };
  }, []);

  // Lock the document while intro owns the stage. Hold used to leave the
  // snap track free — after title→intro return, down-scroll moved `scrollY`
  // through chapter panes while the fixed note sat still. Yield only to the
  // note scroller (`data-hub-nested-scroll`).
  useEffect(() => {
    if (
      introPhase !== "hold" &&
      introPhase !== "wiping" &&
      introPhase !== "returning"
    ) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        return;
      }
      if (introPhaseRef.current === "hold") {
        const deltaY = wheelDeltaY(event);
        if (nestedScrollerCanAbsorb(event.target, deltaY)) {
          return;
        }
      }
      event.preventDefault();
    };
    let touchY: number | null = null;
    const onTouchStart = (event: TouchEvent) => {
      touchY = event.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY;
      if (touchY == null || y == null) {
        return;
      }
      const deltaY = touchY - y;
      if (Math.abs(deltaY) < 8) {
        return;
      }
      if (introPhaseRef.current === "hold") {
        if (nestedScrollerCanAbsorb(event.target, deltaY)) {
          touchY = y;
          return;
        }
      }
      event.preventDefault();
      touchY = y;
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [introPhase]);

  useEffect(() => {
    if (!motionOn) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          '[role="dialog"], input, textarea, select, [contenteditable="true"]',
        )
      ) {
        return;
      }
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        go(1);
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        go(-1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [go, motionOn]);

  /**
   * One chapter per wheel / swipe. CSS snap alone lets trackpad momentum skip
   * title on the way back from Loudoun; the old title-up handler then jumped
   * straight to the intro. Own the gesture so each flick lands on the neighbor.
   */
  useEffect(() => {
    if (!motionOn) {
      return;
    }
    let acc = 0;
    let idleTimer: number | null = null;
    let touchY: number | null = null;
    let touchDeltaY = 0;
    let touchConsumed = false;
    /** Swipe that hit commit while nav-locked — fire once the lock drops. */
    let queuedDir: 1 | -1 | null = null;
    let queuedFlushTimer: number | null = null;

    const resetAccSoon = () => {
      if (idleTimer != null) {
        window.clearTimeout(idleTimer);
      }
      idleTimer = window.setTimeout(() => {
        acc = 0;
        idleTimer = null;
      }, NAV_WHEEL_IDLE_MS);
    };

    const clearQueuedFlush = () => {
      if (queuedFlushTimer != null) {
        window.clearTimeout(queuedFlushTimer);
        queuedFlushTimer = null;
      }
    };

    const commitDir = (direction: 1 | -1) => {
      if (touchConsumed) {
        return;
      }
      touchConsumed = true;
      queuedDir = null;
      clearQueuedFlush();
      go(direction);
    };

    const queueDir = (direction: 1 | -1) => {
      queuedDir = direction;
      clearQueuedFlush();
      const wait = Math.max(0, navLockUntilRef.current - performance.now()) + 16;
      queuedFlushTimer = window.setTimeout(() => {
        queuedFlushTimer = null;
        const dir = queuedDir;
        queuedDir = null;
        if (dir == null || touchConsumed) {
          return;
        }
        if (performance.now() < navLockUntilRef.current) {
          queueDir(dir);
          return;
        }
        commitDir(dir);
      }, wait);
    };

    const onWheel = (event: WheelEvent) => {
      if (introPhaseRef.current !== "done") {
        return;
      }
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        return;
      }
      const deltaY = wheelDeltaY(event);
      if (deltaY === 0) {
        return;
      }
      if (nestedScrollerCanAbsorb(event.target, deltaY)) {
        return;
      }
      event.preventDefault();
      if (performance.now() < navLockUntilRef.current) {
        acc = 0;
        return;
      }
      acc += deltaY;
      resetAccSoon();
      if (Math.abs(acc) < NAV_WHEEL_THRESHOLD_PX) {
        return;
      }
      const direction = acc > 0 ? 1 : -1;
      acc = 0;
      go(direction);
    };

    const onTouchStart = (event: TouchEvent) => {
      touchY = event.touches[0]?.clientY ?? null;
      touchDeltaY = 0;
      touchConsumed = false;
      queuedDir = null;
      clearQueuedFlush();
      // Fresh park budget — a prior soft-swipe drift used to exhaust the
      // single chrome-park attempt for the rest of the session.
      chromeParkAttemptsRef.current = 0;
      markTouchGesture();
    };
    const onTouchEnd = () => {
      // Lift between prevent and commit — still counts as an intentional flick.
      if (
        !touchConsumed &&
        introPhaseRef.current === "done" &&
        Math.abs(touchDeltaY) >= NAV_TOUCH_COMMIT_PX
      ) {
        const direction: 1 | -1 = touchDeltaY > 0 ? 1 : -1;
        if (performance.now() < navLockUntilRef.current) {
          queueDir(direction);
        } else {
          commitDir(direction);
        }
      } else if (!touchConsumed && introPhaseRef.current === "done") {
        // Soft swipe moved scrollY via snap without a hop — re-park on lift.
        needsChromeParkRef.current = true;
        chromeParkAttemptsRef.current = 0;
      }
      clearTouchGestureSoon();
    };
    const onTouchMove = (event: TouchEvent) => {
      if (introPhaseRef.current !== "done" || touchConsumed) {
        return;
      }
      const y = event.touches[0]?.clientY;
      if (touchY == null || y == null) {
        return;
      }
      const deltaY = touchY - y;
      touchDeltaY = deltaY;
      const absY = Math.abs(deltaY);
      if (absY < NAV_TOUCH_PREVENT_PX) {
        return;
      }
      if (nestedScrollerCanAbsorb(event.target, deltaY)) {
        return;
      }
      event.preventDefault();
      if (absY < NAV_TOUCH_COMMIT_PX) {
        return;
      }
      const direction: 1 | -1 = deltaY > 0 ? 1 : -1;
      if (performance.now() < navLockUntilRef.current) {
        queueDir(direction);
        return;
      }
      commitDir(direction);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      if (idleTimer != null) {
        window.clearTimeout(idleTimer);
      }
      clearQueuedFlush();
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [go, motionOn, markTouchGesture, clearTouchGestureSoon]);

  // Mute hold underlays after enter `fill-lock` has dropped the animation
  // (wipeMs − 64). A longer pad here used to leave a window where a closed
  // overlay pose punched the previous chapter through; muting on the
  // terminate beat raced holes and read as black. Clear on every hop so the
  // live outgoing stays painted for the wipe itself.
  useEffect(() => {
    setWipeEndMute(false);
    if (!motionOn || !outgoingId) {
      return;
    }
    if (!isWipeTransition(outgoingId, activeSlideId)) {
      return;
    }
    const timer = window.setTimeout(() => {
      setWipeEndMute(true);
    }, wipeMs + 48);
    return () => {
      window.clearTimeout(timer);
    };
  }, [motionOn, outgoingId, activeSlideId, wipeMs]);

  // Drop the outgoing marker once the hop has settled. Delayed past wipe end
  // so underlay mute / mediaWarm can shift in a quiet window (nav lock already
  // blocks the next hop until then). Chapter shells stay mounted.
  useEffect(() => {
    if (!motionOn || !outgoingId) {
      return;
    }
    // Chapter→note is not on WIPE_EDGES, but still runs a depart close.
    const wipe =
      isWipeTransition(outgoingId, activeSlideId) ||
      (activeSlideId === "note" &&
        (isWipeChapter(outgoingId) || outgoingId === "credits"));
    // Android: keep leaving/playback stable past the terminate-frame + mute latch.
    const wipeClearPad = getBrowserInfo()?.android ? 700 : 400;
    const timer = window.setTimeout(() => {
      setOutgoingId((current) => (current === outgoingId ? null : current));
    }, wipe ? wipeMs + wipeClearPad : styles.slideFadeMs);
    return () => window.clearTimeout(timer);
  }, [motionOn, outgoingId, activeSlideId, wipeMs]);

  // Prefetch wipe chapter shells one viewport early (posters / chrome). Video
  // `src` still waits on `mediaWarm` / neighborhood. Only deck chapters —
  // warming Arizona while idle on Utah mounted it under `resume` and it
  // painted over Utah (z-index 4) when chrome remounted the deck.
  useEffect(() => {
    if (!motionOn || typeof IntersectionObserver === "undefined") {
      return;
    }
    const pending = deckWipeChapters.filter((id) => !mountedWipes.has(id));
    if (pending.length === 0) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.flatMap((entry) => {
          if (!entry.isIntersecting) {
            return [];
          }
          const id = entry.target.getAttribute("data-slide-id");
          return id &&
            isWipeChapter(id) &&
            deckWipeChapters.includes(id)
            ? [id]
            : [];
        });
        if (hit.length > 0) {
          mountWipes(hit);
        }
      },
      { rootMargin: "100% 0px", threshold: 0 },
    );
    for (const id of pending) {
      const pane = paneRefs.current[id];
      if (pane) {
        observer.observe(pane);
      }
    }
    return () => observer.disconnect();
  }, [motionOn, mountedWipes, mountWipes, deckWipeChapters]);

  // Motion-on: scrollY is a slave of `activeSlideId`. IO only confirms that a
  // `go()` pending hop has landed — it must never promote a chapter from a
  // browser scroll reset (idle / screen-off was flipping to the intro).
  useEffect(() => {
    if (!motionOn || typeof IntersectionObserver === "undefined") {
      return;
    }
    const panes = deck
      .map((slide) => paneRefs.current[slide.id])
      .filter((pane): pane is HTMLDivElement => Boolean(pane));
    if (panes.length === 0) {
      return;
    }

    const ratios = new Map<DeckSlideId, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute("data-slide-id");
          if (id) {
            ratios.set(id as DeckSlideId, entry.intersectionRatio);
          }
        }
        const pending = pendingSlideRef.current;
        if (!pending) {
          return;
        }
        if ((ratios.get(pending) ?? 0) > 0.45) {
          pendingSlideRef.current = null;
          programmaticScrollRef.current = false;
        }
      },
      { threshold: [0.45, 0.55, 0.6, 0.75, 0.9] },
    );
    for (const pane of panes) {
      observer.observe(pane);
    }
    return () => observer.disconnect();
  }, [motionOn, deck]);

  // Any scrollY change we did not ask for is snapped back to the active pane.
  // Chapter hops are wheel / touch / keyboard `go()` only (not scrollbar IO).
  useEffect(() => {
    if (!motionOn) {
      return;
    }
    /** URL-bar show/hide rewrites scrollY — don't fight it mid-resize. */
    let chromeToggleLock = false;
    let chromeToggleClear = 0;
    let deferredParkTimer = 0;
    let verifyParkTimer = 0;
    /** Quiet gap between parks in one drift episode. */
    const PARK_COOLDOWN_MS = 450;
    /** Parks allowed before backing off (severe mid-pane starts a new episode). */
    const PARK_MAX_ATTEMPTS = 4;
    /** Ignore chrome jitter under this fraction of the pane height. */
    const PARK_DRIFT_RATIO = 0.35;
    /** Mid-pane / worse — keep retrying; never silent-give-up. */
    const PARK_SEVERE_RATIO = 0.5;
    const PARK_DEFER_MS = 280;
    const PARK_VERIFY_MS = 360;

    const clearDeferredPark = () => {
      window.clearTimeout(deferredParkTimer);
      deferredParkTimer = 0;
    };

    const scheduleDeferredPark = (delayMs = PARK_DEFER_MS) => {
      if (deferredParkTimer !== 0) {
        return;
      }
      deferredParkTimer = window.setTimeout(() => {
        deferredParkTimer = 0;
        parkActiveIfDrifted();
      }, delayMs);
    };

    const measureDrift = () => {
      const id = activeSlideIdRef.current;
      const pane = paneRefs.current[id];
      if (!pane) {
        return null;
      }
      const delta = Math.abs(window.scrollY - pane.offsetTop);
      const ratio = delta / Math.max(1, pane.offsetHeight);
      return { id, ratio };
    };

    const parkBlocked = () =>
      performance.now() < navLockUntilRef.current ||
      Boolean(pendingSlideRef.current) ||
      programmaticScrollRef.current ||
      touchGestureRef.current ||
      chromeToggleLock;

    const parkActiveIfDrifted = () => {
      if (introPhaseRef.current !== "done") {
        return;
      }
      // Wipe / fade / finger / chrome — leave scroll alone, retry when quiet.
      if (parkBlocked()) {
        needsChromeParkRef.current = true;
        const untilLock = navLockUntilRef.current - performance.now() + 48;
        scheduleDeferredPark(
          Math.min(Math.max(PARK_DEFER_MS, untilLock), 2000),
        );
        return;
      }
      const drift = measureDrift();
      if (!drift) {
        return;
      }
      // Sub-pane chrome jitter — not a real mid-pane rest.
      if (drift.ratio <= PARK_DRIFT_RATIO) {
        needsChromeParkRef.current = false;
        chromeParkAttemptsRef.current = 0;
        clearDeferredPark();
        return;
      }

      const severe = drift.ratio >= PARK_SEVERE_RATIO;
      const now = performance.now();
      if (now - chromeParkLastAtRef.current < PARK_COOLDOWN_MS) {
        needsChromeParkRef.current = true;
        scheduleDeferredPark(
          PARK_COOLDOWN_MS - (now - chromeParkLastAtRef.current) + 16,
        );
        return;
      }
      if (chromeParkAttemptsRef.current >= PARK_MAX_ATTEMPTS) {
        if (severe) {
          // Still between panes — new episode after a longer quiet wait.
          chromeParkAttemptsRef.current = 0;
          needsChromeParkRef.current = true;
          scheduleDeferredPark(PARK_COOLDOWN_MS * 2);
        } else {
          // Mild leftover jitter — wait for the next gesture to reset budget.
          needsChromeParkRef.current = false;
        }
        return;
      }

      chromeParkAttemptsRef.current += 1;
      chromeParkLastAtRef.current = now;
      needsChromeParkRef.current = false;
      parkScrollTo(drift.id, "auto");

      // iOS sometimes rewrites scrollY after our park — verify and re-queue.
      window.clearTimeout(verifyParkTimer);
      verifyParkTimer = window.setTimeout(() => {
        verifyParkTimer = 0;
        if (introPhaseRef.current !== "done" || parkBlocked()) {
          needsChromeParkRef.current = true;
          scheduleDeferredPark();
          return;
        }
        const after = measureDrift();
        if (after && after.ratio > PARK_DRIFT_RATIO) {
          needsChromeParkRef.current = true;
          scheduleDeferredPark(PARK_DEFER_MS);
        }
      }, PARK_VERIFY_MS);
    };

    flushChromeParkRef.current = () => {
      parkActiveIfDrifted();
    };

    const onVisualViewportResize = () => {
      // A finger-driven swipe often toggles chrome; never start the re-park
      // timer across that gesture — mark dirty for touch-end flush only.
      if (touchGestureRef.current) {
        needsChromeParkRef.current = true;
        return;
      }
      // Mid-hop chrome toggle: remember drift, do not park until nav lock ends.
      if (performance.now() < navLockUntilRef.current) {
        needsChromeParkRef.current = true;
        scheduleDeferredPark(
          Math.min(
            Math.max(PARK_DEFER_MS, navLockUntilRef.current - performance.now() + 48),
            2000,
          ),
        );
        return;
      }
      chromeToggleLock = true;
      window.clearTimeout(chromeToggleClear);
      // Re-park when the toolbar finishes so snap IO cannot promote a neighbor.
      chromeToggleClear = window.setTimeout(() => {
        chromeToggleLock = false;
        // Never yank scroll during an in-flight chapter hop — that was a
        // mid-wipe flash when smooth park hid the URL bar.
        if (
          pendingSlideRef.current ||
          programmaticScrollRef.current ||
          touchGestureRef.current ||
          performance.now() < navLockUntilRef.current
        ) {
          needsChromeParkRef.current = true;
          scheduleDeferredPark();
          return;
        }
        parkActiveIfDrifted();
      }, 600);
    };
    const vv = window.visualViewport;
    vv?.addEventListener("resize", onVisualViewportResize);

    const syncScrollToActive = () => {
      if (programmaticScrollRef.current || pendingSlideRef.current) {
        needsChromeParkRef.current = true;
        scheduleDeferredPark();
        return;
      }
      if (chromeToggleLock || performance.now() < navLockUntilRef.current) {
        needsChromeParkRef.current = true;
        scheduleDeferredPark(
          Math.min(
            Math.max(PARK_DEFER_MS, navLockUntilRef.current - performance.now() + 48),
            2000,
          ),
        );
        return;
      }
      parkActiveIfDrifted();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        chromeParkAttemptsRef.current = 0;
        syncScrollToActive();
      }
    };
    window.addEventListener("scroll", syncScrollToActive, { passive: true });
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", syncScrollToActive);
    return () => {
      flushChromeParkRef.current = () => {};
      window.removeEventListener("scroll", syncScrollToActive);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", syncScrollToActive);
      vv?.removeEventListener("resize", onVisualViewportResize);
      window.clearTimeout(chromeToggleClear);
      window.clearTimeout(programmaticScrollClearRef.current);
      clearDeferredPark();
      window.clearTimeout(verifyParkTimer);
    };
  }, [motionOn, parkScrollTo]);

  // Motion-off: IntersectionObserver picks the slide in view, note included.
  useEffect(() => {
    if (motionOn || typeof IntersectionObserver === "undefined") {
      return;
    }
    const nodes = deck
      .map((slide) => document.getElementById(`hub-slide-${slide.id}`))
      .filter((node): node is HTMLElement => Boolean(node));
    if (nodes.length === 0) {
      return;
    }
    const ratios = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target.id, entry.intersectionRatio);
        }
        let bestId: DeckSlideId | null = null;
        let bestRatio = 0;
        for (const slide of deck) {
          const ratio = ratios.get(`hub-slide-${slide.id}`) ?? 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = slide.id;
          }
        }
        if (
          bestId &&
          bestId !== activeSlideIdRef.current &&
          bestRatio > 0.45
        ) {
          onActiveSlideChange(bestId);
        }
      },
      { threshold: [0.45, 0.6, 0.75] },
    );
    for (const node of nodes) {
      observer.observe(node);
    }
    return () => observer.disconnect();
  }, [motionOn, deck, onActiveSlideChange]);

  if (!motionOn) {
    return (
      <div
        className={styles.viewportStatic}
        data-hub-scroll="static"
        role="region"
        aria-roledescription="carousel"
        aria-labelledby={labelId}
      >
        <p id={labelId} className={styles.srOnly}>
          The Center Will Not Hold — {activeLabel}
        </p>
        {hasNote ? (
          <StaticNote
            blocks={editorNoteBlocks}
            active={activeSlideId === "note"}
            onSlideLink={goTo}
          />
        ) : null}
        {slides.map((slide) => {
          const active = slide.id === activeSlideId;
          return (
            <section
              key={slide.id}
              id={`hub-slide-${slide.id}`}
              className={styles.stageStatic}
              aria-hidden={active ? undefined : true}
            >
              <div className={styles.stageCanvas}>
                {slide.id === "title" ? (
                  // Always "active" for paint — no intro covers / wipes; posters
                  // show because TitleSlide treats motion-off as frozen media.
                  <TitleSlide cells={slide.cells} active />
                ) : slide.id === "virginia" ? (
                  <LoudounSlide
                    active={active}
                    animate={false}
                    featureHref={slide.featureHref}
                    loudounMeta={loudounMeta}
                  />
                ) : slide.id === "utah" ? (
                  <UtahSlide
                    active={active}
                    animate={false}
                    featureHref={slide.featureHref}
                    utahMeta={utahMeta}
                  />
                ) : (
                  <ArizonaSlide
                    active={active}
                    animate={false}
                    featureHref={slide.featureHref}
                    arizonaMeta={arizonaMeta}
                  />
                )}
              </div>
            </section>
          );
        })}
        <section
          id="hub-slide-credits"
          className={styles.stageStaticFlow}
          aria-hidden={activeSlideId === "credits" ? undefined : true}
        >
          <CreditsSlide
            active={activeSlideId === "credits"}
            animate={false}
          />
        </section>
      </div>
    );
  }

  const loudoun = wipeCoverage(outgoingId, activeSlideId, "virginia");
  const utahWipe = wipeCoverage(outgoingId, activeSlideId, "utah");
  const arizonaWipe = wipeCoverage(outgoingId, activeSlideId, "arizona");
  const coverUnderlay =
    introPhase === "wiping" || introPhase === "returning"
      ? introCoverUnderlay(introLandingRef.current)
      : null;
  /**
   * Desktop: keep the chapter painted under the closing cover squares.
   * Narrow return does not hold — the chapter departs and the note (already
   * under the stage) shows through the closing clip; title stays muted.
   */
  const coverHoldChapter = coverUnderlay != null && !narrow;
  const loudounHold =
    loudoun.hold || (coverHoldChapter && coverUnderlay === "virginia");
  const utahHold =
    utahWipe.hold || (coverHoldChapter && coverUnderlay === "utah");
  const arizonaHold =
    arizonaWipe.hold || (coverHoldChapter && coverUnderlay === "arizona");
  // Cover return: keep the underlay in the warm set and mark it `leaving` so
  // `mediaPlayback` (hold && leaving) stays true until the squares finish —
  // otherwise note/title neighborhood drops Utah/Arizona (and even Virginia
  // can lose `leaving` when outgoing clears) and the video pauses on frame one.
  const mediaWarmSet = (() => {
    if (!mediaNeighborhood) {
      return null;
    }
    if (
      !coverHoldChapter ||
      !coverUnderlay ||
      mediaNeighborhood.has(coverUnderlay)
    ) {
      return mediaNeighborhood;
    }
    const next = new Set(mediaNeighborhood);
    next.add(coverUnderlay);
    return next;
  })();
  const coverLeaving = (id: WipeChapterId) =>
    outgoingId === id || (coverHoldChapter && coverUnderlay === id);
  const showWipe = (id: WipeChapterId) =>
    mountedWipes.has(id) ||
    activeSlideId === id ||
    outgoingId === id ||
    coverUnderlay === id ||
    // Credits only neighborhoods Arizona — keep it mounted as the underlay
    // for the whole credits stop, not just while outgoing is set.
    (id === "arizona" && activeSlideId === "credits");
  // Title mosaic under Loudoun’s enter wipe from the note/title only — not
  // for the whole Virginia stop (and never after a later chapter owns the stage).
  // Stay mounted through wipe-end: gating on `wipeEndMute` unmounted the
  // underlay on the terminate frame and flashed black (title→virginia).
  // Keep the mosaic + IntroCovers mounted for the whole cover wipe — including
  // chapter skips that promote Loudoun/etc. under the squares mid-animation.
  // `hasNote` keeps the intro host alive while chapters are up so returning
  // to the note does not remount (scroll depth + drawn state outlines).
  const titleUnderlayLive =
    hasNote ||
    introPhase !== "done" ||
    activeSlideId === "title" ||
    activeSlideId === "note" ||
    (activeSlideId === "virginia" &&
      (outgoingId === "title" || outgoingId === "note"));

  /**
   * Only the slides in the live transition may paint. Everything else stays
   * mounted (prefetch / back-nav) but muted until the wipe ends.
   * - Intro cover wipe → landing chapter only (or none when landing is title).
   * - Chapter hop → outgoing + active only.
   * Title uses the same rule via `slideUnderlayDormant` — it stays mounted
   * for the note (scroll/SVG) but was still painting under chapter skips.
   */
  const transitionMute = (id: WipeChapterId): boolean => {
    if (introPhase === "wiping" || introPhase === "returning") {
      // Title→note on phone: mute chapters under the closing title cells.
      if (narrow && introPhase === "returning" && !coverUnderlay) {
        return true;
      }
      if (!coverUnderlay) {
        return true;
      }
      return id !== coverUnderlay;
    }
    if (
      outgoingId != null &&
      isWipeTransition(outgoingId, activeSlideId)
    ) {
      return id !== activeSlideId && id !== outgoingId;
    }
    const coverage =
      id === "virginia"
        ? loudoun
        : id === "utah"
          ? utahWipe
          : arizonaWipe;
    // Credits is opaque and z-index 5 over Arizona. wipeEndMute under credits
    // hid AZ at enter-end, then the unmute flashed it at credits→AZ start.
    if (id === "arizona" && activeSlideId === "credits") {
      return false;
    }
    return (
      coverage.hold &&
      (wipeEndMute || (outgoingId != null && outgoingId !== id))
    );
  };

  /** Title mosaic: unmute only when note/title owns the stage or the hop. */
  const titleMuted = (() => {
    if (introPhase === "wiping" || introPhase === "returning") {
      // Chapter under the intro cover — mute the mosaic (desktop + narrow).
      if (coverUnderlay) {
        return true;
      }
      // Narrow leave / title→note: title cells own the wipe.
      if (narrow) {
        return false;
      }
      const landing = introLandingRef.current;
      return landing !== "title" && landing !== "note";
    }
    if (
      outgoingId != null &&
      isWipeTransition(outgoingId, activeSlideId)
    ) {
      const inPair = (id: DeckSlideId) =>
        id === outgoingId || id === activeSlideId;
      return !(inPair("title") || inPair("note"));
    }
    return activeSlideId !== "title" && activeSlideId !== "note";
  })();

  // Note shares the title mosaic. Credits keeps Arizona’s still — the credits
  // center wipe already paints into the under-stage strip, so unmounting here
  // was a hard cut to black instead of riding that sweep.
  //
  // Follow `activeSlideId` during intro wipes/returns so the strip swipes in
  // parallel with the covers (deep link) or chapter depart (Intro). Holding
  // the previous still through the phase deferred the hop until settle and
  // read as a hard pop in lvh mode; parking the note *before* return
  // collapsed `lvh − svh` and popped the strip out first.
  const ballastSlideId =
    activeSlideId === "note"
      ? "title"
      : activeSlideId === "credits"
        ? "arizona"
        : activeSlideId;

  return (
    <div
      data-hub-scroll="snap"
      data-hub-android={getBrowserInfo()?.android ? "" : undefined}
      data-hub-narrow={narrow ? "" : undefined}
      data-hub-motion={motionOn ? "on" : "off"}
      data-hub-active={activeSlideId}
      role="region"
      aria-roledescription="carousel"
      aria-labelledby={labelId}
    >
      <p id={labelId} className={styles.srOnly}>
        The Center Will Not Hold — {activeLabel}
      </p>

      {ballastSlideId ? <ScrollBallast slideId={ballastSlideId} /> : null}

      <div
        className={clsx(
          styles.stageFixed,
          // Keep overflow visible for the whole credits close — gating on
          // `activeSlideId` alone snapped `overflow: hidden` + `translateZ`
          // on the first depart frame and clipped the panel mid-wipe.
          (activeSlideId === "credits" || outgoingId === "credits") &&
            styles.stageFixedCredits,
        )}
      >
        <div className={styles.viewport}>
          {/* Unmount when dormant — a live title mosaic (desktop panels) was
              able to flash black squares through chapter wipes when chrome
              flipped the narrow media query. */}
          {titleSlide && titleUnderlayLive ? (
            <div
              className={clsx(
                styles.slide,
                styles.slideUnderlay,
                titleMuted && styles.slideUnderlayDormant,
                activeSlideId === "title" && styles.slideActive,
              )}
              aria-hidden={activeSlideId === "title" ? undefined : true}
            >
              <TitleSlide
                cells={titleSlide.cells}
                active={
                  activeSlideId === "title" ||
                  activeSlideId === "note" ||
                  introPhase !== "done"
                }
                editorNoteBlocks={editorNoteBlocks}
                introPhase={introPhase}
                onIntroStart={startIntro}
                onIntroDone={finishIntro}
                onIntroSettled={settleIntro}
                onSlideLink={goTo}
                restoredScroll={noteScrollRef.current}
                onNoteScroll={rememberNoteScroll}
              />
            </div>
          ) : null}

          {showWipe("virginia") ? (
            <LoudounSlide
              active={activeSlideId === "virginia"}
              animate
              hold={loudounHold}
              depart={loudoun.depart}
              leaving={coverLeaving("virginia")}
              // Hold / session restore only. Intro chapter skips must play
              // enter — mobile has no cover-square wipe, so resume looked like
              // a hard cut once the note sheet dropped under the stage.
              resume={
                loudounHold || (resumeMount && activeSlideId === "virginia")
              }
              muted={transitionMute("virginia")}
              mediaWarm={!mediaWarmSet || mediaWarmSet.has("virginia")}
              featureHref={virginia?.featureHref}
              loudounMeta={loudounMeta}
            />
          ) : null}

          {showWipe("utah") ? (
            <UtahSlide
              active={activeSlideId === "utah"}
              animate
              hold={utahHold}
              depart={utahWipe.depart}
              leaving={coverLeaving("utah")}
              resume={utahHold || (resumeMount && activeSlideId === "utah")}
              muted={transitionMute("utah")}
              mediaWarm={!mediaWarmSet || mediaWarmSet.has("utah")}
              featureHref={utah?.featureHref}
              utahMeta={utahMeta}
            />
          ) : null}

          {showWipe("arizona") ? (
            <ArizonaSlide
              active={activeSlideId === "arizona"}
              animate
              hold={arizonaHold}
              depart={arizonaWipe.depart}
              leaving={coverLeaving("arizona")}
              resume={
                arizonaHold || (resumeMount && activeSlideId === "arizona")
              }
              muted={transitionMute("arizona")}
              mediaWarm={!mediaWarmSet || mediaWarmSet.has("arizona")}
              featureHref={arizona?.featureHref}
              arizonaMeta={arizonaMeta}
            />
          ) : null}
          <CreditsSlide active={activeSlideId === "credits"} animate />
        </div>
      </div>

      <div className={styles.snapTrack} aria-hidden="true">
        {deck.map((slide) => (
          <div
            key={slide.id}
            ref={bindPaneRef.current[slide.id]}
            data-slide-id={slide.id}
            className={
              slide.id === "credits"
                ? styles.snapPaneCredits
                : styles.snapPane
            }
          />
        ))}
      </div>
    </div>
  );
};

export default SlideDeck;

/**
 * Active chapter for the page shell. Restores from sessionStorage after soft
 * remounts so an idle tab freeze cannot dump the reader back on the intro.
 * `ready` is false until that restore has run — withhold the deck so the
 * default `"note"` frame never paints.
 */
export function useHubSlideState(
  initial: DeckSlideId = "note",
): [DeckSlideId, Dispatch<SetStateAction<DeckSlideId>>, boolean] {
  const [slideId, setSlideId] = useState<DeckSlideId>(initial);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const stored = readStoredHubSlide();
    if (stored) {
      setSlideId(stored);
    }
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) {
      return;
    }
    writeStoredHubSlide(slideId);
  }, [slideId, ready]);

  return [slideId, setSlideId, ready];
}
